import { usePersistedState } from "@/lib/persistedFilters";
// UC CDR → Domains. Every domain seen in the CDRs, the partner it belongs to,
// and the three things that decide its bill: rate plan, currency, rating source.
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, RefreshCw, Search } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Pill } from "./ui";

const CURRENCIES = ["", "CAD", "USD"];
const SOURCES = [["", "auto"], ["ns", "UC Platform"], ["sbc", "SBC"]];

export default function Domains() {
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ total: 0, unmatched: 0, plans: [] });
  const [q, setQ] = usePersistedState("uccdr-domains.q", "");
  const [unmatched, setUnmatched] = useState(false);
  const [edit, setEdit] = useState({});
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    const { data } = await api.get("/uc-cdr/domains", { params: { q, unmatched } });
    setRows(data.domains || []);
    setMeta({ total: data.total || 0, unmatched: data.unmatched || 0, plans: data.plans || [] });
  }, [q, unmatched]);

  useEffect(() => { load(); }, [load]);

  const sync = async () => {
    setBusy("sync");
    try {
      const { data } = await api.post("/uc-cdr/domains/sync");
      toast.success(`${data.domains} domains · ${data.unmatched} without a partner match`);
      await load();
    } finally {
      setBusy("");
    }
  };

  const save = async (domain) => {
    setBusy(domain);
    try {
      await api.patch(`/uc-cdr/domains/${encodeURIComponent(domain)}`, edit[domain] || {});
      toast.success(`${domain} saved — re-rate the period to apply it`);
      setEdit((e) => ({ ...e, [domain]: undefined }));
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save that domain");
    } finally {
      setBusy("");
    }
  };

  const change = (domain, row, field) => (e) =>
    setEdit((s) => ({ ...s, [domain]: { ...pending(row, s[domain]), [field]: e.target.value } }));

  const pending = (row, draft) => draft || {
    plan_override: row.plan_override || "", currency_override: row.currency_override || "",
    source_override: row.source_override || "",
  };

  return (
    <div className="space-y-4" data-testid="uc-cdr-domains">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
          <Input className="w-64 pl-8" placeholder="Domain, partner or territory" value={q}
                 data-testid="uc-cdr-domain-search" onChange={(e) => setQ(e.target.value)} />
        </div>
        <Button size="sm" variant={unmatched ? "default" : "outline"} data-testid="uc-cdr-domain-unmatched"
                onClick={() => setUnmatched((v) => !v)}>
          No partner match ({meta.unmatched.toLocaleString()})
        </Button>
        <span className="text-xs text-slate-500" data-testid="uc-cdr-domain-count">
          {rows.length.toLocaleString()} of {meta.total.toLocaleString()} domains
        </span>
        <Button size="sm" variant="outline" className="ml-auto" onClick={sync} disabled={!!busy}
                data-testid="uc-cdr-domain-sync">
          {busy === "sync" ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Re-scan domains
        </Button>
      </div>

      <p className="max-w-3xl text-sm text-slate-500">
        A domain's plan comes from its partner's type (Service Provider → Service Provider,
        Reseller → Reseller, anything else → Standard). Override any of the three columns when a
        customer is billed differently; a domain with no partner match falls back to Standard/CAD.
      </p>

      <Card className="overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead className="text-[11px] uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-3 py-1.5">Domain</th>
              <th className="px-3 py-1.5">Partner</th>
              <th className="px-3 py-1.5">Type</th>
              <th className="px-3 py-1.5">Plan</th>
              <th className="px-3 py-1.5">Currency</th>
              <th className="px-3 py-1.5">Rate from</th>
              <th className="px-3 py-1.5">Legs</th>
              <th className="px-3 py-1.5" />
            </tr>
          </thead>
          <tbody data-testid="uc-cdr-domains-body">
            {rows.map((r) => {
              const draft = edit[r.domain];
              const cur = pending(r, draft);
              return (
                <tr key={r.domain} className="border-t border-slate-100">
                  <td className="px-3 py-1.5 font-medium text-slate-800">{r.domain}</td>
                  <td className="px-3 py-1.5 text-slate-600">
                    {r.org_name || <span className="text-amber-600">{r.territory || "unknown"}</span>}
                  </td>
                  <td className="px-3 py-1.5">
                    {r.partner_type ? <Pill>{r.partner_type}</Pill> : <span className="text-slate-400">—</span>}
                  </td>
                  <td className="px-3 py-1.5">
                    <select value={cur.plan_override} onChange={change(r.domain, r, "plan_override")}
                            data-testid={`uc-cdr-domain-plan-${r.domain}`}
                            className="h-7 w-44 rounded border border-slate-200 bg-white px-1 text-[12px]">
                      <option value="">{`auto · ${r.plan_auto}`}</option>
                      {meta.plans.map((p) => <option key={p} value={p}>{p}</option>)}
                    </select>
                  </td>
                  <td className="px-3 py-1.5">
                    <select value={cur.currency_override} onChange={change(r.domain, r, "currency_override")}
                            data-testid={`uc-cdr-domain-currency-${r.domain}`}
                            className="h-7 w-24 rounded border border-slate-200 bg-white px-1 text-[12px]">
                      {CURRENCIES.map((c) => <option key={c || "auto"} value={c}>{c || "auto · CAD"}</option>)}
                    </select>
                  </td>
                  <td className="px-3 py-1.5">
                    <select value={cur.source_override} onChange={change(r.domain, r, "source_override")}
                            data-testid={`uc-cdr-domain-source-${r.domain}`}
                            className="h-7 w-32 rounded border border-slate-200 bg-white px-1 text-[12px]">
                      {SOURCES.map(([v, label]) => (
                        <option key={v || "auto"} value={v}>
                          {v ? label : `auto · ${r.source_auto === "ns" ? "UC Platform" : "SBC"}`}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="px-3 py-1.5 text-slate-600">{(r.calls || 0).toLocaleString()}</td>
                  <td className="px-3 py-1.5 text-right">
                    {draft && (
                      <Button size="sm" onClick={() => save(r.domain)} disabled={busy === r.domain}
                              data-testid={`uc-cdr-domain-save-${r.domain}`}>
                        {busy === r.domain ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
                      </Button>
                    )}
                  </td>
                </tr>
              );
            })}
            {!rows.length && (
              <tr><td colSpan={8} className="px-3 py-8 text-center text-sm text-slate-500"
                      data-testid="uc-cdr-domains-empty">
                No domains yet — ingest some CDRs, then hit "Re-scan domains".
              </td></tr>
            )}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
